import { CommonModule } from '@angular/common';
import { Component, forwardRef, Input, OnChanges, SimpleChanges } from '@angular/core';
import { ControlValueAccessor, FormsModule, NG_VALUE_ACCESSOR } from '@angular/forms';
import { IftaLabelModule } from 'primeng/iftalabel';
import { InputGroupModule } from 'primeng/inputgroup';
import { InputGroupAddonModule } from 'primeng/inputgroupaddon';
import { SelectModule } from 'primeng/select';
import { catchError, finalize, of } from 'rxjs';
import { Country } from '../../models/common/country.model';
import { BaseMasterDataService } from '../../services/base-master-data.service';

/**
 * Shared, country-aware currency selector based on the existing country list.
 */
@Component({
    selector: 'app-country-currency-select',
    standalone: true,
    imports: [CommonModule, FormsModule, IftaLabelModule, InputGroupModule, InputGroupAddonModule, SelectModule],
    template: `
        <p-inputgroup>
            <p-inputgroup-addon><i class="pi pi-wallet"></i></p-inputgroup-addon>
            <p-iftalabel>
                <p-select
                    [inputId]="inputId"
                    [options]="options"
                    optionLabel="label"
                    optionValue="code"
                    [ngModel]="value"
                    [ngModelOptions]="{ standalone: true }"
                    (ngModelChange)="selectValue($event)"
                    [filter]="options.length > 8"
                    filterBy="label,code"
                    [showClear]="!required"
                    [loading]="loading"
                    [disabled]="disabled"
                    appendTo="body"
                    styleClass="w-full">
                </p-select>
                <label [for]="inputId">{{ label }}@if (required) { <b>*</b> }</label>
            </p-iftalabel>
        </p-inputgroup>
    `,
    providers: [{
        provide: NG_VALUE_ACCESSOR,
        useExisting: forwardRef(() => CountryCurrencySelectComponent),
        multi: true
    }]
})
export class CountryCurrencySelectComponent implements ControlValueAccessor, OnChanges {
    @Input() countryId?: number | null;
    @Input() label = 'Currency';
    @Input() inputId = 'currencyCode';
    @Input() required = false;

    options: Array<Country & { code: string; label: string }> = [];
    value: string | null = null;
    loading = false;
    disabled = false;

    private onChange: (value: string | null) => void = () => undefined;
    private onTouched: () => void = () => undefined;

    constructor(private masterData: BaseMasterDataService) {}

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['countryId']) {
            this.loadCountries();
        }
    }

    writeValue(value: string | null | undefined): void {
        this.value = value?.trim().toUpperCase() || null;
    }

    registerOnChange(fn: (value: string | null) => void): void {
        this.onChange = fn;
    }

    registerOnTouched(fn: () => void): void {
        this.onTouched = fn;
    }

    setDisabledState(disabled: boolean): void {
        this.disabled = disabled;
    }

    selectValue(value: string | null): void {
        this.value = value || null;
        this.onChange(this.value);
        this.onTouched();
    }

    private loadCountries(): void {
        this.loading = true;
        this.masterData.getCountries().pipe(
            catchError(() => of([] as Country[])),
            finalize(() => this.loading = false)
        ).subscribe(countries => {
            const scopedCountries = this.countryId
                ? (countries ?? []).filter(country => country.id === this.countryId)
                : countries ?? [];
            this.options = scopedCountries
                .filter(country => !!country.currencyCode?.trim())
                .map(country => ({
                    ...country,
                    code: country.currencyCode.trim().toUpperCase(),
                    label: this.optionLabel(country)
                }));
            if (!this.options.length) {
                return;
            }
            const currentIsAvailable = this.options.some(option => option.code === this.value);
            if (!currentIsAvailable || this.countryId) {
                this.selectValue(this.options[0].code);
            }
        });
    }

    private optionLabel(country: Country): string {
        const code = country.currencyCode.trim().toUpperCase();
        const descriptor = country.currencyLong || country.name;
        const symbol = country.currencySymbol ? ` (${country.currencySymbol})` : '';
        return descriptor ? `${code} — ${descriptor}${symbol}` : `${code}${symbol}`;
    }
}
